import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import DataTable from "@/components/mui/DataTable";

export default function CompanyList() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [generatingPix, setGeneratingPix] = useState(false);
  const [pixData, setPixData] = useState(null);

  // Paginação client-side
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/admin/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast.error("Erro ao carregar empresas");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePix = async (companyId) => {
    setGeneratingPix(true);
    try {
      const response = await axios.post(`${API}/payments/companies/${companyId}/generate-pix`);
      setPixData(response.data);
      toast.success("QR Code PIX gerado com sucesso!");
    } catch (error) {
      toast.error("Erro ao gerar PIX");
    } finally {
      setGeneratingPix(false);
    }
  };

  const handleToggleActive = async (companyId) => {
    try {
      await axios.post(`${API}/admin/companies/${companyId}/toggle-active`);
      toast.success("Status da empresa atualizado!");
      fetchCompanies();
    } catch (error) {
      toast.error("Erro ao atualizar empresa");
    }
  };

  const getTrialDaysRemaining = (trialEndsAt) => {
    if (!trialEndsAt) return null;
    const endDate = new Date(trialEndsAt);
    if (Number.isNaN(endDate.getTime())) return null;
    const diffMs = endDate.getTime() - Date.now();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  };

  const getStatusChip = (company) => {
    const status = company.payment_status;
    const statusConfig = {
      periodo_teste: { label: "Período de Teste", color: "warning" },
      em_dia: { label: "Em Dia", color: "success" },
      em_atraso: { label: "Em Atraso", color: "error" },
      bloqueada: { label: "Bloqueada", color: "error" },
    };

    const config = statusConfig[status] || {
      label: status,
      color: "default",
    };

    let label = config.label;
    if (status === "periodo_teste") {
      const remainingDays = getTrialDaysRemaining(company.trial_ends_at);
      if (remainingDays !== null) {
        label = `${config.label} (${remainingDays} dia${remainingDays === 1 ? "" : "s"})`;
      }
    }

    return (
      <Chip
        label={label}
        color={config.color}
        size="small"
        variant={config.color === "default" ? "outlined" : "filled"}
      />
    );
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
  };

  if (loading && companies.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // fatia local para a página atual (DataTable não faz o slice)
  const paginatedCompanies =
    rowsPerPage > 0
      ? companies.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage
        )
      : companies;

  // colunas do DataTable
  const columns = [
    {
      id: "name",
      label: "Empresa",
      render: (company) => (
        <Typography variant="body2" fontWeight={600} noWrap>
          {company.name}
        </Typography>
      ),
    },
    {
      id: "email",
      label: "Email",
    },
    {
      id: "cnpj",
      label: "CNPJ",
      render: (company) => company.cnpj || "-",
    },
    {
      id: "payment_status",
      label: "Status Pagamento",
      render: (company) => getStatusChip(company),
    },
    {
      id: "users_count",
      label: "Usuários",
      align: "center",
      render: (company) => company.users_count || 0,
    },
    {
      id: "active",
      label: "Status",
      render: (company) => (
        <Chip
          label={company.active ? "Ativa" : "Inativa"}
          color={company.active ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      id: "actions",
      label: "Ações",
      align: "center",
      render: (company) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <Tooltip title="Ver Detalhes">
            <IconButton
              size="small"
              onClick={() =>
                navigate(`/admin/companies/${company.id}`)
              }
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() =>
                navigate(`/admin/companies/${company.id}/edit`)
              }
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Gerar Cobrança PIX">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setSelectedCompany(company);
                setShowPaymentDialog(true);
              }}
            >
              <MoneyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={company.active ? "Desativar" : "Ativar"}>
            <IconButton
              size="small"
              color={company.active ? "error" : "success"}
              onClick={() => handleToggleActive(company.id)}
            >
              {company.active ? (
                <BlockIcon fontSize="small" />
              ) : (
                <CheckCircleIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const tablePagination = {
    page,
    rowsPerPage,
    total: companies.length,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [5, 10, 25, 50, { label: "Todos", value: -1 }],
  };

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Empresas Cadastradas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {companies.length} empresa(s) no sistema
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/admin/companies/new")}
        >
          Nova Empresa
        </Button>
      </Box>

      {/* Tabela dentro do Card */}
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: "0 10px 40px rgba(15,23,42,0.04)",
        }}
      >
        {companies.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Nenhuma empresa cadastrada
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Clique em &quot;Nova Empresa&quot; para cadastrar a primeira.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/admin/companies/new")}
            >
              Nova Empresa
            </Button>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            rows={paginatedCompanies}
            loading={loading}
            pagination={tablePagination}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `company-row-${row.id}`}
            emptyMessage="Nenhuma empresa cadastrada"
          />
        )}
      </Card>

      {/* Dialog Gerar PIX */}
      <Dialog
        open={showPaymentDialog}
        onClose={() => {
          setShowPaymentDialog(false);
          setPixData(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Gerar Cobrança PIX - {selectedCompany?.name}
        </DialogTitle>
        <DialogContent>
          {!pixData ? (
            <Box textAlign="center" py={3}>
              <Typography variant="body1" gutterBottom>
                Valor da mensalidade: <strong>R$ 90,00</strong>
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                mb={3}
              >
                Será gerado um QR Code PIX para pagamento
              </Typography>
              <Button
                variant="contained"
                onClick={() => handleGeneratePix(selectedCompany?.id)}
                disabled={generatingPix}
                startIcon={
                  generatingPix ? (
                    <CircularProgress size={20} />
                  ) : (
                    <MoneyIcon />
                  )
                }
              >
                {generatingPix ? "Gerando..." : "Gerar QR Code PIX"}
              </Button>
            </Box>
          ) : (
            <Box textAlign="center" py={2}>
              <Typography
                variant="h6"
                gutterBottom
                color="success.main"
              >
                QR Code Gerado!
              </Typography>
              {pixData.qr_code_base64 && (
                <Box display="flex" justifyContent="center" my={3}>
                  <img
                    src={`data:image/png;base64,${pixData.qr_code_base64}`}
                    alt="QR Code PIX"
                    style={{ maxWidth: "300px", width: "100%" }}
                  />
                </Box>
              )}
              {pixData.qr_code && (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Código Copia e Cola:
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: "grey.100",
                      p: 2,
                      borderRadius: 2,
                      wordBreak: "break-all",
                      fontSize: "0.85rem",
                      fontFamily: "monospace",
                    }}
                  >
                    {pixData.qr_code}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2 }}
                    onClick={() => {
                      navigator.clipboard.writeText(pixData.qr_code);
                      toast.success("Código copiado!");
                    }}
                  >
                    Copiar Código
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowPaymentDialog(false);
              setPixData(null);
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
